import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { analyticsModule } from '@/store/analytics/module'
import { userModule } from '@/store/user/module'

const nestApiDomain = import.meta.env.VUE_APP_NEST_API_URL_DOMAIN
const nestApiVersion = import.meta.env.VUE_APP_NEST_API_URL_VERSION
const hmacSecret = import.meta.env.VUE_APP_NEST_API_HMACS_SECRET

/**
 * Generates an HMAC signature for authenticating requests to the nest API
 * @param unixTimestamp Date.now() in seconds, as a string
 * @param method HTTP method (e.g. 'GET', 'POST', etc)
 * @param fullpath Full path of the request, including version and endpoint
 * @param body Request body stringified as JSON (if applicable)
 * @returns HMAC signature as a string
 */
async function generateHMAC (unixTimestamp: string, method: string, fullpath: string, body = ''): Promise<string> {
    const encoder = new TextEncoder()
    const keyData = encoder.encode(hmacSecret)
    const message = unixTimestamp + method + fullpath + body
    const messageData = encoder.encode(message)

    const cryptoKey = await crypto.subtle.importKey(
        'raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, [ 'sign' ]
    )

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)
    return Array.from(new Uint8Array(signature)).map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * server-nest (Server 2.0) renames the id field.
 * Maps these fields for consistency with Parse Server convention
 *
 * @param {T} object - Object returned from the nest API
 * @returns {T & { objectId: string }} - Object with objectId field
 */
export const mapObjectIdField = <T extends { id: string }>(object: T): T & { objectId: string } => {
    return {
        ...object,
        objectId: object.id,
    }
}

/**
 * Builds the headers every nest request carries: the HMAC signature (over timestamp, method, full path,
 * and body), the Parse session token, and the Amplitude session/device ids.
 * Each call signs with a fresh timestamp, so call it once per request — including each stream reconnect.
 */
const buildNestRequestHeaders = async ({ method, fullPath, body }: {
    method: string
    fullPath: string
    body: string
}) => {
    const unixTimestamp = String(Math.floor(Date.now() / 1000))
    const hmacSignature = await generateHMAC(unixTimestamp, method, fullPath, body)
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Timestamp': unixTimestamp,
        'X-Signature': hmacSignature,
    }
    const sessionToken = userModule.getters.getSessionToken()
    if (sessionToken) {
        headers['X-Parse-Session-Token'] = sessionToken
    }

    // Include amplitude session id, if present
    const ampSessionId = analyticsModule.actions.getAmplitudeSessionId()
    if (ampSessionId) {
        headers['X-Session-Id'] = String(ampSessionId)
    }

    const ampDeviceId = analyticsModule.actions.getAmplitudeDeviceId()
    if (ampDeviceId) {
        headers['X-Device-Id'] = ampDeviceId
    }
    return headers
}

/**
 * Signs a GET for a nest `text/event-stream` endpoint, to be read with `fetch`.
 * `EventSource` can't send the HMAC and session headers, so streams can't go through it (or axios).
 *
 * @param {string} path - Endpoint path without the version prefix (e.g. '/live-games/abc/events')
 * @returns {Promise<{ url: string, headers: Record<string, string> }>} - Absolute URL and signed headers
 */
export const buildSignedNestStreamRequest = async (path: string) => {
    const fullPath = `${nestApiVersion}${path}`
    const headers = await buildNestRequestHeaders({ method: 'GET', fullPath, body: '' })
    return {
        url: `${nestApiDomain}${fullPath}`,
        headers: {
            ...headers,
            'Accept': 'text/event-stream',
        },
    }
}

/**
 * Calls the nest API
 *
 * @param {AxiosRequestConfig} config - Axios config, with URL set to the endpoint path (e.g. '/level-up/eligibility')
 * @returns {Promise<AxiosResponse['data']>} - response.data from API, or error if request fails
 */
export const callApi = async (config: AxiosRequestConfig) => {
    const { data, method = 'GET' } = config
    const fullPath = `${nestApiVersion}${axios.getUri(config)}`
    const headers = await buildNestRequestHeaders({
        method,
        fullPath,
        body: data ? JSON.stringify(data) : '',
    })
    try {
        const request = await axios({
            ...config,
            baseURL: nestApiDomain + nestApiVersion,
            headers,
        })
        return request?.data as AxiosResponse['data']
    } catch (err) {
        if ((err as AxiosError<{ error: string | Record<string, unknown> }>)?.response?.data?.error) {
            const errorMessage = (err as AxiosError<{ error: string }>)?.response?.data.error
            if (errorMessage && typeof errorMessage === 'object' && 'errorMessage' in errorMessage) {
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw errorMessage
            }
            // `cause` keeps the response (status, and any machine-readable `code` in the body) for callers
            throw Object.assign(new Error(errorMessage), { cause: err })
        } else {
            throw err
        }
    }
}
